import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Check } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';
import StepPersonalInfo from './StepPersonalInfo';
import StepProfessionalInfo from './StepProfessionalInfo';
import StepPreferences from './StepPreferences';

const STEPS = [
  { label: 'Personal', component: StepPersonalInfo },
  { label: 'Professional', component: StepProfessionalInfo },
  { label: 'Preferences', component: StepPreferences },
];

const CandidateOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    known_as: '',
    phone: '',
    location_city: '',
    location_state: '',
    location_country: 'Australia',
    citizenship_status: '',
    clearance_level: '',
    notice_period: '',
    employment_types: [],
    payroll_type: '',
    pty_ltd_abn: '',
    cv_file: null,
    availability_status: 'open',
    availability_open_date: '',
    open_to_new_roles: true,
    open_to_client_contact: true,
    preferred_contact_channel: 'email',
    social_links: [],
    skills: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [candidateId, setCandidateId] = useState(null);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    loadExistingData();
  }, []);

  const loadExistingData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: candidate } = await supabase
        .from('candidates')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (candidate) {
        if (candidate.onboarding_complete) {
          navigate('/candidate', { replace: true });
          return;
        }
        setCandidateId(candidate.id);
        setFormData(prev => ({
          ...prev,
          first_name: candidate.first_name || prev.first_name,
          last_name: candidate.last_name || prev.last_name,
          known_as: candidate.known_as || '',
          phone: candidate.phone || '',
          location_city: candidate.location_city || '',
          location_state: candidate.location_state || '',
          location_country: candidate.location_country || 'Australia',
          citizenship_status: candidate.citizenship_status || '',
          clearance_level: candidate.clearance_level || '',
          notice_period: candidate.notice_period || '',
          employment_types: Array.isArray(candidate.employment_types) ? candidate.employment_types : [],
          payroll_type: candidate.payroll_type || '',
          pty_ltd_abn: candidate.pty_ltd_abn || '',
          availability_status: candidate.availability_status || 'open',
          availability_open_date: candidate.availability_open_date || '',
          open_to_new_roles: candidate.open_to_new_roles !== false,
          open_to_client_contact: candidate.open_to_client_contact !== false,
          preferred_contact_channel: candidate.preferred_contact_channel || 'email',
          social_links: Array.isArray(candidate.social_links) ? candidate.social_links : [],
        }));

        // Load skills already on the profile - typically confirmed during a public
        // job application before this account existed. Without this the wizard would
        // re-ask about them and then try to insert duplicates on finish.
        const { data: existingSkills } = await supabase
          .from('candidate_skills')
          .select('id, skill_id, level, skill:skills_master(id, name, category)')
          .eq('candidate_id', candidate.id);

        if (existingSkills?.length) {
          setFormData(prev => ({
            ...prev,
            skills: existingSkills
              .filter(cs => cs.skill)
              .map(cs => ({
                id: cs.skill.id,
                name: cs.skill.name,
                category: cs.skill.category,
                level: cs.level,
                existing: true,
              })),
          }));
        }
      }
    } catch (err) {
      console.error('Error loading candidate data:', err);
    } finally {
      setLoading(false);
    }
  };

  const isStep1Valid = () => {
    return (
      formData.first_name.trim() &&
      formData.last_name.trim() &&
      formData.phone.trim() &&
      formData.location_city.trim() &&
      formData.location_state
    );
  };

  const handleNext = () => {
    if (currentStep === 0 && !isStep1Valid()) return;
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleComplete = async () => {
    setSaving(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build update payload (exclude non-DB fields)
      // Determine if payroll preference should be saved (only for contractor/casual types)
      const contractorTypes = ['Contractor – Full-time', 'Contractor – Part-time', 'Casual'];
      const hasContractorType = formData.employment_types.some(t => contractorTypes.includes(t));

      const updateData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        known_as: formData.known_as.trim() || null,
        phone: formData.phone.trim(),
        location_city: formData.location_city.trim(),
        location_state: formData.location_state,
        location_country: formData.location_country.trim() || 'Australia',
        citizenship_status: formData.citizenship_status || null,
        clearance_level: formData.clearance_level || null,
        notice_period: formData.notice_period.trim() || null,
        employment_types: formData.employment_types,
        payroll_type: hasContractorType ? (formData.payroll_type || null) : null,
        pty_ltd_abn: hasContractorType && formData.payroll_type === 'PTY LTD' ? (formData.pty_ltd_abn.trim() || null) : null,
        availability_status: formData.availability_status || 'open',
        availability_open_date: formData.availability_status === 'open_soon' ? (formData.availability_open_date || null) : null,
        open_to_new_roles: formData.open_to_new_roles,
        open_to_client_contact: formData.open_to_client_contact,
        preferred_contact_channel: formData.preferred_contact_channel || 'email',
        social_links: formData.social_links,
        onboarding_complete: true,
        updated_at: new Date().toISOString(),
      };

      // Update candidate record
      const { data: updatedCandidate, error: updateError } = await supabase
        .from('candidates')
        .update(updateData)
        .eq('user_id', user.id)
        .select('id')
        .single();

      if (updateError) throw updateError;

      const cid = updatedCandidate?.id || candidateId;

      // Upload resume if provided
      if (formData.cv_file && cid) {
        const fileExt = formData.cv_file.name.split('.').pop();
        const fileName = `${cid}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('candidate-files')
          .upload(fileName, formData.cv_file);

        if (uploadError) {
          console.error('CV upload error:', uploadError);
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('candidate-files')
            .getPublicUrl(fileName);

          const { error: insertError } = await supabase.from('candidate_files').insert({
            candidate_id: cid,
            file_type: 'cv',
            file_name: formData.cv_file.name,
            file_url: publicUrl,
            file_size: formData.cv_file.size,
          });

          if (insertError) {
            console.error('CV file record insert error:', insertError);
          }
        }
      }

      // Save skills. Anything marked `existing` is already on the profile (loaded in
      // loadExistingData) - re-inserting it would violate the unique index and, before
      // that index existed, silently created duplicates.
      const newSkills = (formData.skills || []).filter(s => !s.existing);
      if (newSkills.length > 0 && cid) {
        for (const skill of newSkills) {
          if (skill.isPending) {
            // Create pending skill via RPC
            await supabase.rpc('add_candidate_skill', {
              p_candidate_id: cid,
              p_skill_name: skill.name,
              p_level: skill.level || 'Intermediate',
            });
          } else {
            // Link existing catalogue skill
            await supabase.from('candidate_skills').upsert(
              {
                candidate_id: cid,
                skill_id: skill.id,
                level: skill.level || 'Intermediate',
                source: skill.fromCv ? 'cv_extract' : 'manual',
                confirmed_at: skill.fromCv ? new Date().toISOString() : null,
              },
              { onConflict: 'candidate_id,skill_id', ignoreDuplicates: true }
            );
          }
        }
      }

      // If the user came from the apply page via OAuth, send them back there
      const returnUrl = sessionStorage.getItem('oauth_return_url');
      if (returnUrl) {
        sessionStorage.removeItem('oauth_return_url');
        navigate(returnUrl, { replace: true });
      } else {
        navigate('/candidate', { replace: true });
      }
    } catch (err) {
      console.error('Error saving onboarding data:', err);
      setError(err.message || 'Failed to save your details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkipAndFinish = async () => {
    await handleComplete();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const StepComponent = STEPS[currentStep].component;
  const isLastStep = currentStep === STEPS.length - 1;
  const canContinue = currentStep === 0 ? isStep1Valid() : true;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/appsytec-logo.png"
              alt="Appsytec Logo"
              className="h-8 w-auto"
            />
          </div>
          <span className="text-sm text-muted-foreground">
            Step {currentStep + 1} of {STEPS.length}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-3 md:p-8">
        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.label}>
              <div className="flex flex-col items-center">
                <div
                  className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index < currentStep
                      ? 'bg-primary text-white'
                      : index === currentStep
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className={`text-xs mt-1.5 ${
                  index <= currentStep ? 'text-primary font-medium' : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`h-0.5 w-12 md:w-20 mx-2 mt-[-12px] transition-colors ${
                    index < currentStep ? 'bg-primary' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step Content */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentStep}
                initial={{ x: direction * 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction * -30, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <StepComponent formData={formData} setFormData={setFormData} />
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <div>
            {currentStep > 0 && (
              <Button variant="ghost" onClick={handleBack} disabled={saving}>
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Skip link for steps 2 and 3 */}
            {currentStep > 0 && !isLastStep && (
              <Button variant="ghost" size="sm" onClick={handleNext} disabled={saving}>
                Skip this step
              </Button>
            )}
            {isLastStep && (
              <Button variant="ghost" size="sm" onClick={handleSkipAndFinish} disabled={saving}>
                {saving ? 'Saving...' : 'Skip and finish'}
              </Button>
            )}

            {!isLastStep ? (
              <Button onClick={handleNext} disabled={!canContinue || saving}>
                Continue
              </Button>
            ) : (
              <Button onClick={handleComplete} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateOnboarding;
